export type PushPermissionStatus = 'granted' | 'denied' | 'undetermined';

type EnablePushDeviceRegistrationParams = {
  getPermissionStatus: () => Promise<PushPermissionStatus>;
  requestPermission: () => Promise<PushPermissionStatus>;
  getToken: () => Promise<string | null>;
  registerToken: (token: string) => Promise<void>;
  trackPermission: (granted: boolean) => void;
};

export type EnablePushDeviceRegistrationResult =
  | { ok: true; status: PushPermissionStatus }
  | { ok: false; reason: 'permission' | 'token' | 'registration'; status: PushPermissionStatus };

export async function enablePushDeviceRegistration({
  getPermissionStatus,
  requestPermission,
  getToken,
  registerToken,
  trackPermission,
}: EnablePushDeviceRegistrationParams): Promise<EnablePushDeviceRegistrationResult> {
  const previousStatus = await getPermissionStatus().catch(() => 'undetermined' as const);
  // react-doctor-disable-next-line react-doctor/server-sequential-independent-await
  const status = await requestPermission().catch(() => 'denied' as const);

  // Permission analytics should represent the first OS prompt outcome, not retries from settings.
  if (previousStatus === 'undetermined') {
    trackPermission(status === 'granted');
  }

  if (status !== 'granted') {
    return { ok: false, reason: 'permission', status };
  }

  const token = await getToken();
  if (!token) {
    return { ok: false, reason: 'token', status };
  }

  try {
    await registerToken(token);
  } catch {
    return { ok: false, reason: 'registration', status };
  }

  return { ok: true, status };
}

type SyncCurrentPushTokenParams = {
  userID: string;
  registeredKey: string | null;
  getPermissionStatus: () => Promise<PushPermissionStatus>;
  getToken: () => Promise<string | null>;
  registerToken: (token: string) => Promise<void>;
};

export async function syncCurrentPushToken({
  userID,
  registeredKey,
  getPermissionStatus,
  getToken,
  registerToken,
}: SyncCurrentPushTokenParams): Promise<{ registeredKey: string | null; retry: boolean }> {
  const permissionStatus = await getPermissionStatus().catch(() => 'undetermined' as const);
  if (permissionStatus !== 'granted') {
    return { registeredKey: null, retry: false };
  }

  const token = await getToken().catch(() => null);
  if (!token) {
    return { registeredKey: null, retry: true };
  }

  const nextKey = `${userID}:${token}`;
  if (registeredKey === nextKey) {
    return { registeredKey, retry: false };
  }

  try {
    await registerToken(token);
    return { registeredKey: nextKey, retry: false };
  } catch {
    return { registeredKey: null, retry: true };
  }
}
